/**
 * Plan copy — the single code-defined wording of what the Resida plan, the listing offering and
 * the agent support add-on include (BR-113).
 *
 * Served unchanged by GET /api/public/plan-copy, imported directly by the marketing site's
 * prerender and by the landlord payment-settings form. Prices, the listing period and the trial
 * length are NOT here: they come from the plan data (GET /api/public/pricing). The only figure is
 * the per-payment ACH fee, which is fixed by Stripe's ACH pricing cap rather than plan data.
 *
 * Runtime imports: zod only, and no window/process access, so the marketing site can import it
 * during its SSR prerender.
 */
import { z } from 'zod';
/**
 * Per-payment ACH fee, in cents. Fixed by Stripe's ACH pricing cap, so it is intentionally a
 * constant here rather than plan configuration.
 */
export const ACH_PAYMENT_FEE_CENTS = 500;
/** Whole-dollar display of a cent amount, e.g. 500 -> "$5". */
function dollars(cents) {
    return `$${cents / 100}`;
}
/** One bullet of plan copy. */
export const PlanCopyPointSchema = z.object({
    name: z.string(),
    /** Optional supporting line, shown in muted text under the name. */
    description: z.string().optional(),
    /** Slug of the marketing feature page the bullet describes, e.g. `rent-collection`. */
    featureSlug: z.string().optional(),
});
const PlanCopySectionSchema = z.object({
    title: z.string(),
    points: z.array(PlanCopyPointSchema),
});
export const PlanCopyResponseSchema = z.object({
    /** What the base per-unit Resida plan includes. */
    included: z.array(PlanCopyPointSchema),
    /** The optional per-listing offering. */
    listing: PlanCopySectionSchema,
    /** The optional per-unit agent support add-on. */
    agent: PlanCopySectionSchema,
});
/**
 * Appended to a plan's listing bullets on the marketing pricing page when the free trial includes
 * listings. Plan-bullet wording, so it lives here with the rest of the plan copy (BR-113); it is not
 * part of the served PLAN_COPY because the landlord surface never shows trial terms.
 */
export const TRIAL_LISTING_POINT = { name: 'Available during your free trial' };
export const PLAN_COPY = {
    included: [
        {
            name: 'Rent collection via ACH and Zelle',
            description: `ACH payments are ${dollars(ACH_PAYMENT_FEE_CENTS)} each`,
            featureSlug: 'rent-collection',
        },
        { name: 'Lease creation with guided wizard', featureSlug: 'lease-management' },
        { name: 'Built-in e-signatures (no third-party fees)', featureSlug: 'document-signing' },
        { name: 'Tenant messaging (in-app chat + email)', featureSlug: 'tenant-communication' },
        { name: 'Maintenance request tracking', featureSlug: 'maintenance' },
        { name: 'AI expense categorization (IRS Schedule E)', featureSlug: 'expense-tracking' },
        { name: 'Tax-ready expense report export', featureSlug: 'expense-tracking' },
        { name: 'Attention dashboard across every unit', featureSlug: 'dashboard' },
        { name: 'Calendar, tasks, and reminders' },
        { name: 'Tenant portal (no app download required)' },
    ],
    listing: {
        title: 'Listings',
        points: [
            { name: 'Assisted posting on Craigslist and Facebook' },
            { name: 'AI leasing agent included' },
            { name: 'Pay only when a unit is vacant' },
            { name: 'Pausing and relisting are free' },
        ],
    },
    agent: {
        title: 'Agent support · add-on',
        points: [
            { name: 'AI agents triage repair requests' },
            { name: 'Coordinate vendors and keep everyone updated' },
            { name: 'Answer tenant messages' },
            { name: 'You approve anything that costs money' },
        ],
    },
};
//# sourceMappingURL=index.js.map